/** InsCoder WANG.J 2026-07-23 B + Mobile in-app browser detection and notice controls **/
(() => {
  const selector = '[data-app-browsing]';
  const chatSelector = '#tidio-chat';
  const dismissalCookieName = 'cust_app_browsing_dismissed';
  const dismissalCookieMaxAge = 60 * 60 * 24;
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');
  const mobileMedia = window.matchMedia('(max-width: 767px)');
  const hiddenChatElements = new Map();
  const previousFocusByNotice = new WeakMap();

  const hasDismissalCookie = () =>
    document.cookie
      .split('; ')
      .some((cookie) => cookie.startsWith(`${dismissalCookieName}=`));

  const saveDismissalCookie = () => {
    const secureAttribute = window.location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = `${dismissalCookieName}=1; Max-Age=${dismissalCookieMaxAge}; Path=/; SameSite=Lax${secureAttribute}`;
  };

  const hideChatElement = (chatElement) => {
    if (!hiddenChatElements.has(chatElement)) {
      hiddenChatElements.set(chatElement, {
        priority: chatElement.style.getPropertyPriority('visibility'),
        value: chatElement.style.getPropertyValue('visibility'),
      });
    }

    if (
      chatElement.style.getPropertyValue('visibility') !== 'hidden' ||
      chatElement.style.getPropertyPriority('visibility') !== 'important'
    ) {
      chatElement.style.setProperty('visibility', 'hidden', 'important');
    }
  };

  const restoreChatElements = () => {
    hiddenChatElements.forEach(({ priority, value }, chatElement) => {
      if (value) {
        chatElement.style.setProperty('visibility', value, priority);
      } else {
        chatElement.style.removeProperty('visibility');
      }
    });
    hiddenChatElements.clear();
  };

  const hasOpenNotice = () =>
    Array.from(document.querySelectorAll(selector)).some((notice) => !notice.hidden);

  const syncChatVisibility = () => {
    if (!hasOpenNotice()) {
      restoreChatElements();
      return;
    }

    document.querySelectorAll(chatSelector).forEach(hideChatElement);
  };

  document.documentElement.className = document.documentElement.className.replace('no-js', 'js');

  if (window.Shopify && Shopify.designMode) {
    document.documentElement.classList.add('shopify-design-mode');
  }

  const isSupportedAppBrowser = () => {
    const ua = navigator.userAgent;
    const browser = {
      line: /\bLine\//i.test(ua),
      instagram: /Instagram/i.test(ua),
      facebook: /FBAN|FBAV|FB_IAB/i.test(ua),
    };

    return browser.line || browser.instagram || browser.facebook;
  };

  const shouldShowNotice = () => {
    const isDesignMode = Boolean(window.Shopify && Shopify.designMode);
    const isDismissed = !isDesignMode && hasDismissalCookie();
    // const isDismissed = false

    return mobileMedia.matches && !isDismissed && (isDesignMode || isSupportedAppBrowser());
  };

  const getFocusableElements = (notice) =>
    Array.from(notice.querySelectorAll(focusableSelector)).filter(
      (element) => !element.hidden && element.getAttribute('aria-hidden') !== 'true'
    );

  const moveFocusIntoNotice = (notice) => {
    if (!previousFocusByNotice.has(notice)) {
      previousFocusByNotice.set(notice, document.activeElement);
    }

    const [firstFocusableElement] = getFocusableElements(notice);
    firstFocusableElement?.focus({ preventScroll: true });
  };

  const restoreNoticeFocus = (notice) => {
    const previousFocus = previousFocusByNotice.get(notice);
    previousFocusByNotice.delete(notice);

    if (previousFocus instanceof HTMLElement && previousFocus.isConnected) {
      previousFocus.focus({ preventScroll: true });
    }
  };

  const setNoticeVisibility = (notice, shouldShow) => {
    const wasHidden = notice.hidden;
    const isNonModal = notice.dataset.appBrowsingNonModal === 'true';
    notice.hidden = !shouldShow;

    if (shouldShow && wasHidden && !isNonModal) {
      moveFocusIntoNotice(notice);
    } else if (!shouldShow && !wasHidden) {
      restoreNoticeFocus(notice);
    }
  };

  const trapNoticeFocus = (event, notice) => {
    const isNonModal = notice.dataset.appBrowsingNonModal === 'true';
    if (event.key !== 'Tab' || notice.hidden || isNonModal) return;

    const focusableElements = getFocusableElements(notice);
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];

    if (!firstFocusableElement || !lastFocusableElement) {
      event.preventDefault();
      return;
    }

    if (!notice.contains(document.activeElement)) {
      event.preventDefault();
      firstFocusableElement.focus();
    } else if (event.shiftKey && document.activeElement === firstFocusableElement) {
      event.preventDefault();
      lastFocusableElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastFocusableElement) {
      event.preventDefault();
      firstFocusableElement.focus();
    }
  };

  const initNotice = (notice) => {
    if (!notice) return;

    if (notice.dataset.appBrowsingInitialized !== 'true') {
      notice.dataset.appBrowsingInitialized = 'true';
      notice.addEventListener('keydown', (event) => trapNoticeFocus(event, notice));

      const dismissButton = notice.querySelector('[data-app-browsing-dismiss]');
      dismissButton?.addEventListener('click', () => {
        saveDismissalCookie();
        setNoticeVisibility(notice, false);
        syncChatVisibility();
      });
    }

    setNoticeVisibility(notice, shouldShowNotice());
  };

  const initNotices = (root = document) => {
    if (root.matches?.(selector)) initNotice(root);
    root.querySelectorAll?.(selector).forEach(initNotice);
  };

  const refreshNotices = (root = document) => {
    initNotices(root);
    syncChatVisibility();
  };

  refreshNotices();

  mobileMedia.addEventListener('change', () => {
    refreshNotices();
  });

  const chatObserver = new MutationObserver((mutations) => {
    if (!hasOpenNotice()) return;

    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;

        if (node.matches(chatSelector)) hideChatElement(node);
        node.querySelectorAll?.(chatSelector).forEach(hideChatElement);
      }
    }
  });

  chatObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });

  document.addEventListener('shopify:section:load', (event) => {
    refreshNotices(event.target);
  });
})();
/** InsCoder WANG.J 2026-07-23 E + Mobile in-app browser detection and notice controls **/
